<template>
  <b-container fluid
               class="vld-parent">
    <!-- Spinner -->
    <loading :active="loading"
             :is-full-page="false"
             :z-index="1000"/>
    <!-- /Spinner -->

    <!-- Card 1: displays content details in a form -->
    <div class="card">
      <!-- Card 1 - Card header: displays status, project, ID, title, and assignee -->
      <div class="card-header">
        <!-- Card 1 - Card header - Row 1: displays status, project, and ID -->
        <div class="pills-wrap">
          <!-- Disclosure -->
          <div class="disclosure-pill">
            {{ content.disclosureName }}
          </div>
          <!-- /Disclosure -->

          <!-- Content ID -->
          <div class="content-id-pill">{{ content.contentId }}</div>
          <!-- /Content ID -->

          <!-- Status -->
          <b-dropdown :text="getTextOfOptions(content.status)"
                      class="btn-status"
                      :class="getStatusClass(content.status)">
            <b-dropdown-item v-for="option in statusOptions"
                             :key="option.value"
                             :value="option.value"
                             @click="content.status = option.value">{{ option.text }}
            </b-dropdown-item>
          </b-dropdown>
          <!-- /Status -->
        </div>
        <!-- /Card 1 - Card header - Row 1: displays status, project, and ID -->

        <!-- Card 1 - Card header - Row 2: Displays title and assignee -->
        <div class="title-wrap">
          <b-form-row class="align-items-end">
            <!-- Title -->
            <b-col cols="9">
              <b-input-group>
                <b-form-input name="title"
                              label="Title"
                              id="title"
                              placeholder="제목을 입력해주세요"
                              size="xl"
                              class="my-0"
                              style="height: 45px; font-size: 30px; border: none;"
                              v-model="content.title"
                              ref="title"
                              v-validate="{ required: true }"
                              :state="validateState('title')"></b-form-input>
              </b-input-group>
            </b-col>
            <!-- /Title -->

            <!-- Assignee -->
            <b-col cols="3">
              <label for="assignee">담당자 <span class="required">*</span></label>
              <b-form-group>
                <b-form-select
                    id="assignee-list"
                    name="assignee"
                    class="custom-select"
                    ref="assignee"
                    size="sm"
                    :options="assigneeOptions"
                    v-model="content.assignee"
                    v-validate="{ required: true }"
                    :state="validateState('assignee')"
                />
              </b-form-group>
            </b-col>
            <!-- /Assignee -->
          </b-form-row>
        </div>
        <!-- Card 1 - Card header - Row 2: Displays title and assignee -->
      </div>
      <!-- /Card 1 - Card header: displays status, project, ID, title, and assignee -->

      <!-- Card 1 - Card Body - displays detailed information -->
      <div class="card-body mt-4 border-top">

        <!-- Card 1 - Card Body - Row 1: displays GRI levels -->
        <b-form>
          <!-- Section - GRI levels -->
          <section class="form-wrap">
            <!-- Row - GRI Lv1, 2, 3 -->
            <b-form-row>
              <!-- GRI Lv1  -->
              <b-col sm="6"
                     md="4"
                     lg="3">
                <label>ESG</label>
                <b-form-group>
                  <b-form-select
                      id="griLv1"
                      name="griLv1"
                      class="custom-select"
                      size="sm"
                      :options="l1Options"
                      v-model="content.griLv1"
                      @change="onGriLevelChange(1)"
                  />
                </b-form-group>
              </b-col>
              <!-- /GRI Lv1  -->

              <!-- GRI Lv2 -->
              <b-col sm="6"
                     md="4"
                     lg="3">
                <label for="Lv2">GRI 공시 기준</label>
                <b-form-group>
                  <b-form-select
                      id="griLv2"
                      name="griLv2"
                      class="custom-select"
                      size="sm"
                      :options="l2Options"
                      v-model="content.griLv2"
                      :disabled="!content.griLv1"
                      @change="onGriLevelChange(2)"
                  />
                </b-form-group>
              </b-col>
              <!-- /GRI Lv2 -->

              <!-- GRI Lv3 -->
              <b-col sm="6"
                     md="4"
                     lg="3">
                <label for="Lv3">GRI 세부 지표</label>
                <b-form-group>
                  <b-form-select
                      id="griLv3"
                      name="griLv3"
                      class="custom-select"
                      size="sm"
                      :options="l3Options"
                      v-model="content.griLv3"
                      :disabled="!content.griLv2"
                      @change="onGriLevelChange(3)"
                  />
                </b-form-group>
              </b-col>
              <!-- GRI Lv3 -->
            </b-form-row>
            <!-- Row - GRI Lv1, 2, 3 -->
          </section>
          <!-- Card 1 - Card Body - Row 1: displays GRI levels -->

          <!-- Card 1 - Card Body - Row 2: displays 'Start/Due Date' -->
          <section class="form-wrap form-row">
            <b-col sm="12"
                   md="6">
              <label for="start-date">시작 - 종료일자</label>
              <div class="date-wrap">
                <!-- Start date -->
                <b-form-group>
                  <b-input-group class="form-inline">
                    <b-form-input
                        id="start-date"
                        name="start-date"
                        type="date"
                        class="custom-input"
                        v-model="content.startDate"
                        v-validate="dateRule"
                        placeholder="YYYY-MM-DD"
                        :state="validateState('start-date')"
                    />
                  </b-input-group>
                </b-form-group>
                <!-- Start date -->

                <span class="tilde">&#126;</span>

                <!-- Due date -->
                <b-form-group>
                  <b-input-group class="form-inline">
                    <b-form-input
                        id="due-date"
                        name="due-date"
                        type="date"
                        class="custom-input"
                        v-model="content.dueDate"
                        v-validate="dateRule"
                        placeholder="YYYY-MM-DD"
                        :state="validateState('due-date')"
                    />
                  </b-input-group>
                </b-form-group>
                <!-- /Due date -->
              </div>
            </b-col>
          </section>
          <!-- Section - 'Start/Due Date' -->
        </b-form>
        <!-- Form -->
      </div>
      <!-- Card 1 - Card Body: displays detailed information -->
    </div>
    <!-- /Card 1 - displays detailed info in a form -->

    <!-- Card 1.5 - displays ESG data -->
    <div class="esg-data-wrap"
         v-if="['환경', '사회', '지배구조'].includes(content.griLv1)">
      <div class="card"
           :class="{ fold: isFolded.esgData }">
        <!-- Card 1.5 - Card header -->
        <div class="card-header">
          <button type="button"
                  class="btn-card-fold"
                  :class="{ 'fold': isFolded.esgData }"
                  @click="isFolded.esgData = !isFolded.esgData"></button>
          <div class="card-title-area">
            <span class="title-text">ESG 데이터</span>
            <button class="ml-3 btn btn-primary"
                    style="width: 80px; height: 35px;"
                    @click="importEsgData">본문 삽입
            </button>
          </div>
        </div>
        <!-- Card 1.5 - Card header -->

        <!-- Card 1.5 - Card Body -->
        <div class="card-body">
          <environment-data-table id="esg-data"
                                  v-if="content.griLv1 === '환경'"/>
          <social-data-table id="esg-data"
                             v-if="content.griLv1 === '사회'"/>
          <governance-data-table id="esg-data"
                                 v-if="content.griLv1 === '지배구조'"/>
        </div>
        <!-- Card 1.5 - Card Body -->
      </div>
    </div>

    <!-- Card 2 - displays description in a editor -->
    <div class="card description">
      <editor :init="editorOption"
              api-key="6vs6uovdsu692fslp2jsn6giqjgr1lhuq70g1abojnrdbswk"
              v-model="content.description"
              ref="description"/>
      <b-form-textarea
          v-show="false"
          rows="12"
          id="description"
          name="description"
          v-model="content.description"
          :state="validateState('description')"
      />
    </div>
    <!-- Card 2 - displays description in a editor -->

    <!-- Card 3 - displays attached files and image preview -->
    <b-card class="file">
      <!-- File -->
      <b-form-row class="align-items-end">
        <b-col class="mb-0">
          <b-form-group label-for="file-input"
                        class="new-file-label">
            <b-form-file
                multiple
                ref="file-input"
                id="file-input"
                name="file"
                v-model="fileInputs"
                placeholder="파일을 선택하거나 드래그 앤 드롭해주세요"
                class="new-file-input"
            />
          </b-form-group>
        </b-col>
      </b-form-row>
      <!-- /File -->

      <!-- File list -->
      <b-form-group ref="file-list">
        <div class="file-list">
          <template v-for="file in files">
            <div v-if="!file.deleted"
                 :key="file.name + file.id"
                 class="file-item">
              <div class="file-info-and-desc-area">
                <div class="file-info-area">
                  <span class="file-name">{{ file.name }}</span>
                  <span class="file-upload-time">{{ file.creationDate | formatDatetime }}</span>
                </div>
                <div class="file-desc-area">
                  <b-input-group>
                    <b-form-input id="fileDesc"
                                  name="fileDesc"
                                  type="text"
                                  v-model="file.description"
                                  @update="onFileDescChange(file)"
                                  class="custom-input"
                                  placeholder="파일 설명">
                    </b-form-input>
                  </b-input-group>
                </div>
              </div>
              <div class="button-area d-flex align-items-end">
                <b-badge v-if="!file.new"
                         class="btn btn-file-download mr-2"
                         variant="primary"
                         @click="downloadFile(file)">다운로드
                </b-badge>
                <b-badge v-if="isImage(file)"
                         class="btn btn-file-download"
                         @click="openImageInNewWindow(file.url)">미리보기
                </b-badge>
              </div>
              <b-badge variant="danger"
                       class="btn btn-delete"
                       @click="onDeleteFile(file)"/>
            </div>
          </template>
        </div>
      </b-form-group>
      <!-- /File list -->
    </b-card>
    <!-- /Card 3 - displays attached files -->

    <!-- Card 4 - Watcher -->
    <b-row>
      <b-col sm="8"
             md="7"
             lg="6">
        <esg-base-card class="watcher"
                       :footer="false">
          <template #header>
            <div class="title-wrap">
              <watcher-icon class="icon"/>
              <div class="title-text">Watcher</div>
              <span class="total-num"
                    v-if="activeWatchers.length > 0">{{ activeWatchers.length }}</span>
            </div>
          </template>

          <template #default>
            <b-input-group class="search-wrap">
              <b-button v-b-modal:members
                        class="search">
                <div class="btn-inner">
                  <span>Watcher 검색</span>
                  <search-icon/>
                </div>
              </b-button>

              <esg-base-dashboard-modal id="members">
                <esg-base-list
                    name="members"
                    ref="members-list"
                    title="Watcher 추가/삭제"
                    :resource="`esg/disclosure/disclosures/${content.disclosureId}/members`"
                    :selectable="true"
                    :selected-items="watchers"
                    :variable-height="true"
                    size="sm"
                />
              </esg-base-dashboard-modal>
            </b-input-group>

            <div class="tag-list">
              <div class="tag"
                   v-for="watcher in activeWatchers"
                   :key="watcher.userId">
                <img src="@/assets/images/icon_user.png"
                     alt="user profile"
                     class="profile-img">
                <span v-b-tooltip
                      :title="watcher.userId">{{ watcher.userName }}</span>
                <button class="tag-remove"
                        @click="onDeleteWatcher(watcher)"/>
              </div>
            </div>
          </template>
        </esg-base-card>
      </b-col>
    </b-row>
    <!-- /Card 4 - Watcher -->

    <!-- Card 5 - Reply list -->
    <div class="card comment">
      <div class="card-body">
        <!-- Posted Replies -->
        <div class="comment-area">
          <div class="comment-list-wrap">
            <div class="comment-title-area">
              <h3>댓글</h3>
              <span class="total-num">{{ replies.length }}</span>
            </div>
            <div class="comment-list">
              <div v-for="reply in replies"
                   :key="reply.id"
                   class="comment-item">
                <reply :reply="reply"/>
              </div>
            </div>
          </div>
          <div class="comment-reply">
            <!-- New Reply Form-->
            <vue-editor
                :editorOptions="{ modules: { toolbar: true } }"
                v-model="newReply.content"
            />
            <b-form-textarea v-show="false"
                             v-model="newReply.content"/>
          </div>
          <!-- /New Reply Form -->
        </div>
        <!-- /Posted Replies -->

        <!-- Highlight -->
        <div class="highlight-area">
          <div class="comment-title-area">
            <h3>하이라이트</h3>
            <span class="total-num">{{ highlightedReplies.length }}</span>
          </div>
          <div class="comment-list custom-scrollbar">
            <div v-for="reply in highlightedReplies"
                 :key="reply.id"
                 class="comment-item">
              <reply :reply="reply"/>
            </div>
          </div>
        </div>
        <!-- Highlight -->
      </div>
    </div>
    <!-- Card - Reply list -->

    <!-- Buttons -->
    <div class="content-btn-wrap">
      <div class="button-container">
        <esg-base-button @click="onClose"
                         variant="secondary"
                         style="width: 100px">
          닫기
        </esg-base-button>
        <esg-base-button @click="update"
                         variant="primary"
                         style="width: 100px">
          업데이트
        </esg-base-button>
      </div>
      <div class="text-container">
        <span class="d-block text">Created by {{ content.creator }} at {{ content.creationDate }}</span>
        <span class="d-block text"
              v-if="content.updater">Updated by {{ content.updater }} at {{ content.updateDate }}</span>
      </div>
    </div>
    <!-- /Buttons -->
  </b-container>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import Editor from '@tinymce/tinymce-vue';

import griData from "@/components/esg/6. ESG 공시보고/model/gri.json";

import EnvironmentDataTable from "@/components/esg/6. ESG 공시보고/temp/EnvironmentDataTable.vue";
import SocialDataTable from "@/components/esg/6. ESG 공시보고/temp/SocialDataTable.vue";
import GovernanceDataTable from "@/components/esg/6. ESG 공시보고/temp/GovernanceDataTable.vue";

import Reply from '@/components/esg/6. ESG 공시보고/6.3. 공시 보고서 항목/EsgDisclosureContentReply.vue';
import EsgBaseCard from "@/components/esg/base/EsgBaseCard.vue";
import EsgBaseDashboardModal from "@/components/esg/base/EsgBaseDashboardModal.vue";
import EsgBaseList from "@/components/esg/base/EsgBaseList.vue";
import EsgBaseButton from "@/components/esg/base/EsgBaseButton.vue";

import WatcherIcon from "@/components/esg/icons/WatcherIcon.vue";
import SearchIcon from "@/components/esg/icons/SearchIcon.vue";

export default {
  name: 'EsgDisclosureContentDetail',
  props: { id: { type: String, default: "-1" } },
  components: {
    EsgBaseButton,
    EsgBaseList,
    EsgBaseDashboardModal,
    SearchIcon,
    WatcherIcon,
    EsgBaseCard,
    VueEditor, Editor, Reply,
    EnvironmentDataTable,
    SocialDataTable,
    GovernanceDataTable
  },
  data() {
    return {
      loading: false,
      assigneeOptions: [],

      // Disclosure report content
      originalContent: {},
      content: {
        disclosureId: '',
        disclosureName: '',
        contentId: '',
        status: '',
        assignee: '',
        title: '',
        startDate: '',
        dueDate: '',
        description: '',
        griLv1: '',
        griLv2: '',
        griLv3: '',
        creationDate: '',
        creator: '',
        updateDate: '',
        updater: '',
      },

      // Files
      files: [],
      fileInputs: [],

      // Watchers
      watchers: [],

      // Replies
      newReply: {
        contentId: this.id,
        id: null,
        content: '',
        highlight: false,
        creator: '',
        creationDate: '',
        dirty: false
      },
      replies: [],

      // etc.
      dateRule: { required: false, regex: /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/ },
      editorOption: {
        menubar: "file edit view insert format tools table help",
        plugins: [
          'advlist autolink lists link image charmap print preview anchor',
          'searchreplace visualblocks code fullscreen',
          'insertdatetime media table paste code help wordcount'
        ],
        toolbar:
            'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media template link anchor codesample | ltr rtl',
        paste_data_images: true,
        image_advtab: true,
        statusbar: false,
        contextmenu: false,
        height: 500
      },
      showDropdown: {
        project: false
      },
      isFolded: {
        esgData: false,
      },
    };
  },
  watch: {
    fileInputs: {
      deep: true,
      handler() {
        if (!this.fileInputs) return;
        this.fileInputs.forEach(async file => {
          const clone = new File([file], file.name, { type: file.type });
          let fileInfo = {
            id: '',
            name: file.name,
            type: file.type,
            path: '',
            description: '',
            file: clone,
            new: true,
            deleted: false,
            updated: false,
            url: '',
            creationDate: this.$moment(new Date()).format('YYYY-MM-DDTHH:mm:ss'),
          };
          fileInfo.url = this.isImage(fileInfo) ? await this.createFileURL(fileInfo) : null
          this.files = [...this.files, fileInfo];
        });
        this.$refs['file-input'].reset();
        this.fileInputs.length = 0;
      }
    },
    'newReply.content': {
      handler(newValue, oldValue) {
        if (!newValue) {
          this.newReply.dirty = false;
        } else if (newValue !== oldValue) {
          this.newReply.dirty = true;
        }
      }
    }
  },
  computed: {
    highlightedReplies() {
      return this.replies.filter(reply => reply.highlight);
    },
    statusOptions() {
      return this.$store.getters['report/statusOptions']
          .filter(status => status.value && status.update)
          .map(status => ({ ...status, text: status.text.replaceAll("\u00A0", '') }));
    },
    l1Options() {
      return this.getGriOptions(1);
    },
    l2Options() {
      return this.getGriOptions(2);
    },
    l3Options() {
      return this.getGriOptions(3);
    },
    activeWatchers() {
      return this.watchers.filter(watcher => watcher.selected);
    }
  },
  async created() {
    // Custom error messages
    const customDictionary = {
      custom: {
        title: {
          required: '보고서명을 입력해주세요',
        },
        assignee: {
          required: '담당자를 지정해주세요',
        },
        description: {
          required: '보고서에 대한 설명을 입력해주세요',
        },
        'start-date': {
          required: '시작일자를 입력해주세요',
          regex: '형식에 맞춰 시작일자를 입력해주세요',
        },
        'end-date': {
          required: '종료일자를 입력해주세요',
          regex: '형식에 맞춰 종료일자를 입력해주세요',
        },
      }
    };
    this.$validator.localize('kr', customDictionary);
    await this.load();
  },
  methods: {
    async load() {
      try {
        this.loading = true;

        // Fetch content, files, and replies asynchronously
        await Promise.all([
          this.retrieveContent(),
          this.retrieveFiles(),
          this.retrieveWatchers(),
          this.retrieveReplies()
        ])

        // Base on the content details, fetch assignee options
        await this.retrieveAssigneeOptions();

      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async retrieveAssigneeOptions() {
      let response = await axios.get(`esg/disclosure/disclosures/${this.content.disclosureId}/members`, {
        headers: { Authorization: accessToken() }
      });
      let members = response.data.list;
      if (!members) return;
      members.forEach(member => {
        member.value = member.userId;
        member.text = member.userName;
      });
      members.sort((a, b) => a.text.localeCompare(b.text));
      this.assigneeOptions = members;
    },
    async retrieveContent() {
      // Load content detail
      let response = await axios.get(`esg/disclosure/contents/${this.id}`, {
        headers: { Authorization: accessToken() }
      });
      this.content = response.data;

      // Clone the original data to check whether the content is changed when updating
      this.originalContent = JSON.parse(JSON.stringify(response.data));
    },
    async retrieveFiles() {
      // Fetch files
      let response = await axios.get(`esg/disclosure/contents/${this.id}/files`, {
        headers: { Authorization: accessToken() }
      });
      let data = response.data;

      // Add flags and URL
      await Promise.all(data.map(async file => {
        this.$set(file, 'new', false);
        this.$set(file, 'updated', false);
        this.$set(file, 'deleted', false);
        this.$set(file, 'url', this.isImage(file) ? await this.createFileURL(file) : '');
      }));

      this.files = data;
    },
    async retrieveWatchers() {
      let response = await axios.get(`esg/disclosure/contents/${this.id}/watchers`, {
        headers: { Authorization: accessToken() }
      });

      // Add flags for managing watcher list
      response.data.list.forEach(watcher => {
        this.$set(watcher, 'selected', true);
        this.$set(watcher, 'new', false);
      });

      this.watchers = response.data.list;
    },
    async retrieveReplies() {
      let response = await axios.get(`/esg/disclosure/contents/${this.id}/replies`, {
        headers: { Authorization: accessToken() }
      });
      this.replies = response.data;
    },
    async update() {
      try {
        this.loading = true;

        // Validate the form
        await this.$validator.validate();
        if (this.errors && this.errors.items.length > 0) {
          this.showErrorsInToast();
          return;
        }

        // Update content, files, watchers, and replies
        await Promise.all([
          this.updateContent(),
          this.updateReplies(),
          this.updateFiles(),
          this.updateWatchers(),
        ]);

        // Fetch the updated replies and files
        await Promise.all([
          this.retrieveReplies(),
          this.retrieveFiles(),
        ]);

        this.loading = false;

        // Notify the result
        this.showMessageInToast(`항목이 업데이트되었습니다.`);
      } catch (error) {
        this.loading = false;
        await this.showError(error.message);
      } finally {
        this.loading = false;
      }
    },
    async updateContent() {
      // If no field has been changed, return
      if (JSON.stringify(this.originalContent) === JSON.stringify(this.content)) return;

      // Else, update the content
      await axios.patch(`/esg/disclosure/contents/${this.id}`, this.content, {
        headers: { Authorization: accessToken() }
      });

      // Update the original data
      this.originalContent = JSON.parse(JSON.stringify(this.content));

      // Send notifications
      await this.sendNotifications('field', `${this.userName()}님이 필드를 수정했습니다`)
    },
    async updateReplies() {
      // Post a new reply
      if (!this.newReply.dirty) return;

      await axios.post(`/esg/disclosure/contents/${this.id}/replies`, [this.newReply], {
        headers: { Authorization: accessToken() }
      });

      // Send notifications
      await this.sendNotifications('reply', `${this.userName()}님이 댓글을 작성했습니다`)

      // Reset the newReply object
      this.newReply = {
        contentId: this.id,
        id: null,
        content: '',
        highlight: false,
        creator: '',
        creationDate: ''
      };
    },
    async updateFiles() {
      let updated = false;

      // List up the files to upload, update, delete
      const filesToUpload = this.files.filter(file => file.new && !file.deleted);
      const filesToUpdate = this.files.filter(file => file.updated && !file.new && !file.deleted);
      const filesToDelete = this.files.filter(file => !file.new && file.deleted);

      // Upload files
      if (filesToUpload.length > 0) {
        // Construct FormData
        let uploadFormData = new FormData();
        filesToUpload.forEach((file, index) => {
          uploadFormData.append(`files[${index}].file`, file.file);
          uploadFormData.append(`files[${index}].type`, file.type);
          uploadFormData.append(`files[${index}].description`, file.description);
        });

        // Upload
        await axios.post(`/esg/disclosure/contents/${this.id}/files`, uploadFormData, {
          headers: { Authorization: accessToken() }
        });

        // Mark updated as true
        updated = true;
      }

      // Update files
      if (filesToUpdate.length > 0) {
        await axios.patch(`/esg/disclosure/files`, filesToUpdate, {
          headers: { Authorization: accessToken() }
        });

        // Mark updated as true
        updated = true;
      }

      // Delete files
      if (filesToDelete.length > 0) {
        await axios.delete(`/esg/disclosure/files`, {
          headers: { Authorization: accessToken() },
          params: { fileIds: filesToDelete.map(file => file.id) }
        });

        // Mark updated as true
        updated = true;
      }

      // Send notifications if updated is true
      if (updated) {
        await this.sendNotifications('file', `${this.userName()}님이 첨부파일을 수정했습니다`)
      }
    },
    async updateWatchers() {
      let updated = false;
      const watchersToInsert = this.watchers.filter(watcher => watcher.new && watcher.selected);
      const watchersToDelete = this.watchers.filter(watcher => !watcher.new && !watcher.selected);

      // Insert watchers
      if (watchersToInsert.length > 0) {
        await axios.post(`/esg/disclosure/contents/${this.id}/watchers`, watchersToInsert, {
          headers: { Authorization: accessToken() }
        });

        watchersToInsert.forEach(watcher => {
          watcher.new = false;
          watcher.selected = true;
        })

        // Mark updated as true
        updated = true;
      }

      // Delete watchers
      if (watchersToDelete.length > 0) {
        const userIds = watchersToDelete.map(watcher => watcher.userId);
        await axios.delete(`/esg/disclosure/contents/${this.id}/watchers`, {
          headers: {
            Authorization: accessToken(),
          },
          params: { userIds }
        });

        // Mark updated as true
        updated = true;
      }

      // Send notifications if updated is true
      if (updated) {
        await this.sendNotifications('watcher', `${this.userName()}님이 Watcher를 변경했습니다`)
      }

      this.watchers = this.watchers.filter(watcher => watcher.selected);
    },
    sendNotifications(type, message) {
      return axios.get(`/esg/disclosure/contents/${this.id}/watchers/notify`, {
        headers: { Authorization: accessToken() },
        params: { type, message }
      });
    },
    async downloadFile(file) {
      try {
        this.loading = true;
        // Create a link
        const link = document.createElement('a');

        // Generate a URL
        let url = await this.createFileURL(file);
        if (!url) throw new Error('파일을 찾을 수 없습니다.');

        // Download the file
        link.href = url;
        link.download = file.name;
        link.click();
      } catch (error) {
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async createFileURL(file) {
      try {
        let data = null;
        if (file.new && file.file) {
          data = file.file;
        } else if (!file.new && file.id) {
          const response = await axios.get(`/esg/disclosure/files/${file.id}/download`, {
            headers: { Authorization: accessToken() },
            responseType: 'blob'
          });
          data = response.data;
        }
        return data ? URL.createObjectURL(data) : null;
      } catch (error) {
        await this.showError(error);
      }
    },
    isImage(file) {
      return !file.deleted && file.type && file.type.includes('image')
    },
    openImageInNewWindow(imageUrl) {
      window.open(imageUrl, '_blank');
    },
    getTextOfOptions(status) {
      const matchedOption = this.statusOptions.find(option => option.value === status);
      return matchedOption ? matchedOption.text : '';
    },
    // 상단의 dropdown-menu 의 상태에 따라 컬러 변경을 위해 클래스 추가
    getStatusClass(status) {
      if (status === 'IN_PROGRESS') return 'in-progress'
      else if (status === 'TO_DO') return 'to-do'
      else if (status === 'DONE') return 'done'
      else if (status === 'CLOSED') return 'closed'
      else if (status === 'CANCELLED') return 'cancelled'
    },
    importEsgData() {
      let esgData = document.getElementById('esg-data')
          .querySelector('.table-container');
      this.content.description += esgData.innerHTML;
    },
    getGriOptions(lv) {
      let result = [];
      if (lv === 1) {
        result = griData.map(datum => datum.griLv1);
      } else if (lv === 2 && this.content.griLv1) {
        result = griData
            .filter(datum => datum.griLv1 === this.content.griLv1)
            .map(datum => datum.griLv2);
      } else if (lv === 3 && this.content.griLv2) {
        result = griData
            .filter(datum => datum.griLv2 === this.content.griLv2)
            .map(datum => datum.griLv3);
      }
      return [...new Set(result)].map(el => ({ text: el, value: el }));
    },
    validateState(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.errors.has(ref);
      }
      return null;
    },
    onGriLevelChange(level) {
      if (level) {
        for (let i = level; i < 3; i++) {
          let idx = i + 1;
          this.content[`griLv${idx}`] = '';
        }
      }
    },
    onFileDescChange(file) {
      file.updated = true;
    },
    onDeleteFile(file) {
      this.$set(file, 'deleted', true);
    },
    onDeleteWatcher(watcher) {
      this.$set(watcher, 'selected', false);
    },
    onClose() {
      window.history.back();
    },
  },
  filters: {
    formatStatus(strStatus) {
      return strStatus.split("_").map(str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()).join(" ");
    },
    formatDatetime(datetime) {
      return !datetime ? null : datetime.split('T').join(" ");
    },
  },
}
</script>

<style scoped>
.text-container {
  white-space: nowrap;
  /* 텍스트가 줄바꿈되지 않도록 설정 */
  overflow: hidden;
  /* 너비를 초과하는 텍스트를 감추기 */
  text-overflow: ellipsis;
  /* 초과된 텍스트를 "..."으로 표기 */
}

.button-container {
  white-space: nowrap;
  /* 텍스트가 줄바꿈되지 않도록 설정 */
  overflow: hidden;
  /* 너비를 초과하는 텍스트를 감추기 */
  margin: 0.2em;
  padding: 0.2em;
}

span.required {
  color: red;
}

.image-title {
  display: block;
  margin-bottom: .3rem;
}

.image-box {
  display: block;
  max-width: 100%;
}

.hide-toolbar .ql-toolbar {
  display: none;
}

.hide-border .ql-container {
  border: none !important;
}

.custom-input {
  height: 2.666rem !important;
  line-height: 1.0 !important;
}

.custom-tags {
  height: 1.1rem !important;
}

.span-90 {
  width: 85%;
}

.b-form-tags .badge {
  display: inline-flex !important;
  align-items: center !important;
  flex: 0 0 auto !important;
  line-height: 1 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  height: 2em;
}

div[contenteditable="true"]:focus {
  outline: none;
}

.col-label {
  display: flex;
  justify-content: flex-end;
}

.tox-tinymce {
  border-radius: 0.6rem !important;
  min-height: 35rem !important;
}

.tox .tox-statusbar {
  display: none;
}

/* 신규 추가 스타일 */
>>> .btn-status .dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 0 0.933rem;
  height: 45px !important;
  padding: 0.266rem 1.066rem 0.266rem 1.6rem !important;
  border: none;
  border-radius: 38px;
  color: #FFF;
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 500;
  line-height: 42px;
}

>>> .btn-status.in-progress .dropdown-toggle,
.btn-status.in-progress .dropdown-toggle.show,
.btn-status.in-progress.show .dropdown-toggle,
.btn-status.in-progress .dropdown-toggle:hover {
  background-color: #FF5C00;
}

>>> .btn-status.to-do .dropdown-toggle,
.btn-status.to-do .dropdown-toggle.show,
.btn-status.to-do.show .dropdown-toggle,
.btn-status.to-do .dropdown-toggle:hover {
  background-color: #747480;
}

>>> .btn-status.done .dropdown-toggle,
.btn-status.done .dropdown-toggle.show,
.btn-status.done.show .dropdown-toggle,
.btn-status.done .dropdown-toggle:hover {
  background-color: #1755CC;
}

>>> .btn-status.closed .dropdown-toggle,
.btn-status.closed .dropdown-toggle.show,
.btn-status.closed.show .dropdown-toggle,
.btn-status.closed .dropdown-toggle:hover {
  background-color: #168D4C;
}

>>> .btn-status.cancelled .dropdown-toggle,
.btn-status.cancelled .dropdown-toggle.show,
.btn-status.cancelled.show .dropdown-toggle,
.btn-status.cancelled .dropdown-toggle:hover {
  background-color: #E4E4EA;
  color: #747480;
}

>>> .btn-status .dropdown-toggle::after {
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-left: 0;
  content: "";
  border: none;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.832 7.5L9.9987 13.3333L4.16536 7.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') center no-repeat;
}

/* Description TinyMCE Editor */
.card.description {
  padding: 1rem;
}

.card.description .tox-tinymce {
  border: none !important;
}


/* comment 영역 */
.card.comment {
  padding: 0;
  position: relative;
  margin-bottom: 2rem;
  border-radius: 30px;
  box-shadow: none;
}

.card.comment .comment-area {
  padding: max(2rem, 1.5vw) 2vw;
}

.card.comment .highlight-area {
  padding: max(2rem, 1.5vw) 2vw;
}

.comment-area .comment-item .card {
  padding: 0;
}

.highlight-area .comment-item .card {
  padding: 0;
}

.card.comment .card-body .form-group {
  margin: 0;
}

.card .comment-reply {
  padding: max(2rem, 1.5vw) 0;
}

>>> .ql-toolbar.ql-snow {
  border-top: 1px solid #DDDDE3;
  border-bottom: 1px solid #DDDDE3;
  border-left: none;
  border-right: none;
}

>>> .ql-container.ql-snow {
  border: none;
}

/* button */
.btn {
  border-radius: 10px;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
}

.btn:focus {
  box-shadow: none;
}

.btn-primary {
  width: 130px;
  height: 60px;
  background-color: #2E2E38;
  color: #FFE600;
  border-color: transparent;
}

.btn-primary:hover,
.btn-primary:active,
.btn-primary:focus {
  color: #FFE600 !important;
  background-color: #0D0D17 !important;
  border-color: transparent !important;
  box-shadow: unset;
}

.btn-primary.disabled,
.btn-primary:disabled {
  color: #9C9CA8 !important;
  background-color: #C4C4CD !important;
  border-color: transparent;
}

.btn-secondary {
  width: 100px;
  height: 60px;
  border-radius: 10px;
  background: #747480;
  color: #fff;
}

.btn-secondary.small {
  height: 40px;
}

.btn-secondary:hover {
  background-color: #565662;
}

.btn-secondary.disabled,
.btn-secondary:disabled {
  color: #9C9CA8;
  background-color: #C4C4CD;
  border-color: transparent;
}

.btn-sub-primary {
  width: 130px;
  height: 60px;
  background-color: #2E2E38;
  border: none;
  color: #FFF;
}

.btn-sub-primary:hover {
  background-color: #0D0D17;
}

.btn-sub-primary.disabled,
.btn-sub-primary:disabled {
  color: #9C9CA8;
  background-color: #C4C4CD;
  border-color: transparent;
}

.btn-line {
  position: relative;
  width: 100px;
  height: 60px;
  border: 1px solid #C4C4CD;
  background-color: transparent;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  text-align: inherit;
}

.btn-line:hover {
  color: #2E2E38;
  border: 1px solid #2E2E38;
  background-color: transparent;
}

.btn-line.disabled,
.btn-line:disabled {
  border: 1px solid #DDDDE3;
  background-color: transparent;
}

.btn-line.small {
  height: 40px;
}

.btn-line.prev {
  padding: 0 1.866rem 0 2.866rem;
}

.btn-line.prev::before {
  content: '';
  display: block;
  position: absolute;
  left: 1.133rem;
  top: 1.466rem;
  width: 16px;
  height: 16px;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.5 2L4.5 8L10.5 14" stroke="%232E2E38" stroke-linecap="round"/></svg>') center no-repeat;
}

.btn-line.prev.disabled::before,
.btn-line.prev:disabled::before {
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.5 2L4.5 8L10.5 14" stroke="%23DDDDE3" stroke-linecap="round"/></svg>') center no-repeat;
}

.btn-line.next {
  padding: 0 2.866rem 0 1.866rem;
}

.btn-line.next::before {
  content: '';
  display: block;
  position: absolute;
  right: 1.133rem;
  top: 1.466rem;
  width: 16px;
  height: 16px;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none"><path d="M6 14L12 8L6 2" stroke="%232E2E38" stroke-linecap="round"/></svg>') center no-repeat;
}

.btn-line.next.disabled::before,
.btn-line.next:disabled::before {
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none"><path d="M6 14L12 8L6 2" stroke="%23DDDDE3" stroke-linecap="round"/></svg>') center no-repeat;
}

.btn-card-fold {
  position: absolute;
  top: 2rem;
  right: 2.666rem;
  display: block;
  width: 30px;
  height: 30px;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M23.75 18.75L15 10L6.25 18.75" stroke="%232E2E38" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>') center no-repeat;
  border: none;
  z-index: 100;
}

.btn-card-fold.fold {
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M23.75 11.25L15 20L6.25 11.25" stroke="%232E2E38" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>') center no-repeat;
}

.btn-file-download {
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #C4C4CD;
  background-color: #FFF;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  margin-top: 1.333rem;
}

.btn-file-download:hover {
  border: 1px solid #747480;
}

/* 공통요소 */
span.required {
  display: inline-block;
  width: 4px;
  height: 4px;
  background-color: #FF0000;
  border-radius: 50%;
  font-size: 0;
}

.border-bottom {
  border-bottom: 1px solid #DDDDE3;
}

.border-top {
  border-top: 1px solid #DDDDE3;
}

.mb-0 {
  margin-bottom: 0 !important;
}

.flex-grow {
  flex-grow: 1;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 20px;
  border-bottom: 30px solid transparent;
  border-right: 14px solid transparent;
  background-clip: padding-box;
}

.custom-scrollbar::-webkit-scrollbar-track {
  width: 6px;
  background-color: #F6F6FA;
  border-bottom: 30px solid transparent;
  border-right: 14px solid transparent;
  background-clip: padding-box;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  width: 100%;
  background-color: #C4C4CD;
  border-bottom: 30px solid transparent;
  border-right: 14px solid transparent;
  background-clip: padding-box;
}

.select-custom-scrollbar::-webkit-scrollbar {
  width: 12px;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid transparent;
  background-clip: padding-box;
}

.select-custom-scrollbar::-webkit-scrollbar-track {
  width: 6px;
  background-color: #E7E7EE;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid transparent;
  background-clip: padding-box;
}

.select-custom-scrollbar::-webkit-scrollbar-thumb {
  width: 100%;
  background-color: #C4C4CD;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid transparent;
  background-clip: padding-box;
}

/* select */
.custom-select {
  padding: 0.5rem 2rem 0.5rem 1rem;
  height: 2.666rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #FFF;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 12px) 9px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  text-overflow: ellipsis;
}

.custom-select:focus {
  border-color: #E7E7EE;
  box-shadow: unset;
}

.custom-select.is-valid {
  border-color: #E7E7EE;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') right 10px center/20px 20px no-repeat,
  url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.5 8L6.01924 11.8392C6.0985 11.9256 6.23483 11.9256 6.3141 11.8392L13.5 4" stroke="%2300A438" stroke-width="2" stroke-linecap="round"/></svg>') center right 2.4rem/16px 16px no-repeat;
}

.custom-select.is-invalid {
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') right 10px center/20px 20px no-repeat,
  url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="8" fill="%23FF0000"/><rect x="7" y="3" width="2" height="7" rx="1" fill="white"/><rect x="7" y="11" width="2" height="2" rx="1" fill="white"/></svg>') center right 2.4rem/16px 16px no-repeat;
}

.custom-select.is-valid:focus {
  box-shadow: none;
  border: 1px solid #00A438;
}

.custom-select.is-invalid:focus {
  box-shadow: none;
  border: 1px solid #FF0000;
}

.custom-select#project-list {
  padding: 0.266rem 0.8rem 0.266rem 1.2rem;
  border-radius: 38px;
  background-color: #E4E4EA;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 12px) 7px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
}

/* Disclosure and Content ID */
.pills-wrap {
  display: flex;
  gap: 0 1rem;
  margin-bottom: 1.5rem;
}

.disclosure-pill,
.content-id-pill {
  display: flex;
  align-items: center;

  width: fit-content;
  height: 45px;
  padding-left: 1.2rem;
  padding-right: 1.2rem;

  border-radius: 38px;
  border: 1px solid transparent;
  background-color: #E4E4EA;

  color: #747480;
  font-size: 18px;
  font-weight: 500;
  font-family: 'NanumGothic';
}

/* input */
.form-group {
  margin-bottom: 0;
}

.title-wrap .form-group {
  margin-bottom: 0;
}

label {
  padding-left: 0.4rem;
  margin-bottom: 0.53rem;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
}

.custom-input,
input[type="date"].form-control,
input[type="time"].form-control,
input[type="datetime-local"].form-control,
input[type="month"].form-control {
  padding: 0.5rem 2rem 0.5rem 1rem;
  height: 2.666rem !important;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #FFF;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150% !important;
}

.custom-input.small {
  width: min(15.866rem, 12.395vw);
}

.form-control#title {
  padding-left: 1px;
  background-color: transparent;
  font-family: 'NanumGothic';
  font-weight: 700;
}

.form-control#title::placeholder {
  font-size: 30px;
}

.form-control:focus {
  box-shadow: none;
  border-color: unset;
}

.form-control.is-valid {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.5 8L6.01924 11.8392C6.0985 11.9256 6.23483 11.9256 6.3141 11.8392L13.5 4" stroke="%2300A438" stroke-width="2" stroke-linecap="round"/></svg>');
}

.form-control.is-invalid {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="8" fill="%23FF0000"/><rect x="7" y="3" width="2" height="7" rx="1" fill="white"/><rect x="7" y="11" width="2" height="2" rx="1" fill="white"/></svg>');
}

.form-control.is-valid:focus {
  box-shadow: 0 0 0 1px #00A438;
}

.form-control.is-invalid:focus {
  box-shadow: 0 0 0 1px #FF0000;
}

.input-group:not(.has-validation) > .form-control:not(:last-child) {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  height: 2.666rem !important;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #F6F6FA;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150% !important;
}

.custom-file {
  height: 4.666rem;
}

/* form */
.form-wrap {
  padding: 2.0833vw 0 0.5208vw;
}

.form-wrap:last-child {
  padding: 2.0833vw 0 0;
}

.form-row > .col,
.form-row > [class*="col-"] {
  padding-right: 0.78125vw;
  padding-left: 0.7812vw;
  margin-bottom: 1.5625vw;
}

.invalid-feedback {
  position: absolute;
  margin-top: 2px;
  padding-left: 6px;
  color: #FF0000;
  font-family: 'NanumGothic';
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
}

/* card 영역 */
.card {
  padding: max(2rem, 1.5vw) 2vw;
  margin-bottom: 2rem;
  border-radius: 30px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.05);
}

.card-header {
  padding: 0;
  border-bottom: none;
}

.card-body {
  padding: 0;
}

.card .card-body.fold {
  height: 0;
  min-height: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  border-top: none !important;
}

.card .title-wrap {
  margin-top: -0.666rem;
}

/* 날짜 영역 / work hour 영역 */
.date-wrap {
  display: flex;
  align-items: center;
  gap: 0 0.666rem;
}

.date-wrap .tilde {
  color: #747480;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
}

/* 파일 업로드 / 파일 리스트 영역 */
.card.file {
  padding: unset;
}

.card.file .card-body {
  padding: 2.666rem 2.0833vw;
}

.new-file-label label {
  padding-left: 0.4rem;
  margin-bottom: 0.53rem;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
}

.new-file-input .custom-file-input {
  height: 100%;
}

.new-file-input .custom-file-input ~ .custom-file-label[data-browse]::after {
  content: none;
}

.new-file-input .custom-file-input.focus {
  border: none;
  box-shadow: none;
}

.new-file-input .custom-file-label {
  height: 100%;
  padding: 0.533rem 1.066rem;
  margin-bottom: 0;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #F6F6FA;
}

.new-file-input .custom-file-label span {
  color: #747480;
  font-family: 'NanumGothic';
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
}

.file-upload-btn-wrap {
  padding-left: 0.7812vw;
}

.file-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 1.333rem;
}

.file-item {
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  min-height: 280px;
  padding: 1.333rem;
  border-radius: 20px;
  border: 1px solid #C4C4CD;
  background-color: #fff;
}

.file-item:hover {
  border-color: #2E2E38;
}

.file-info-area {
  padding-right: 24px;
}

.file-info-area .file-name {
  display: -webkit-box;
  width: 100%;
  height: 2rem;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-all;
}

.file-info-area .file-upload-time {
  display: block;
  margin-top: 0.133rem;
  color: #747480;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
}

.file-info-area .desc-tag-list {
  display: flex;
  gap: 0.666rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;
  margin-bottom: 1.4rem;
  padding: 0;
}

.file-info-area .desc-tag-list .desc-tag-item {
  width: fit-content;
  max-width: 100%;
  padding: 0.3rem 0.8rem;
  list-style: none;
  border-radius: 60px;
  background-color: #F6F6FA;
  color: #747480;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-desc-area {
  margin: 0.8rem 0 0.666rem;
}

.file-item .btn-delete {
  position: absolute;
  top: 1.333rem;
  right: 1.333rem;
  display: block;
  width: 20px;
  height: 20px;
  font-size: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 15L5 5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M15 5L5 15" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') center no-repeat transparent;
}

.card.file .file-item .image-item {
  position: relative;
  width: 70px;
  height: 70px;
  padding-bottom: 100%;
  border-radius: 6px;
  overflow: hidden;
}

.card.file .file-item .image-item > div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.card.file .file-item .image-item > div:hover:after {
  content: '';
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
}

.card.file .file-item .image-item .image-box {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border: unset;
}

/* Watcher 영역 */
.card.watcher >>> .card-header {
  border-bottom: none;
}

.card.watcher .title-wrap {
  display: flex;
  align-items: center;
  gap: 0 0.533rem;
}

.card.watcher .title-wrap .icon {
  display: block;
  width: 24px;
  height: 24px;
}

.card.watcher .title-wrap .title-text {
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
}

.card.watcher .title-wrap .total-num {
  min-width: 36px;
  padding: 2px 8px;
  border-radius: 40px;
  background-color: #FFE500;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  text-align: center;
}

.card.watcher .search-wrap .search.btn {
  width: 100%;
  height: 40px;
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background: #FFF;
  color: #AAA;
  font-family: 'NanumGothic';
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
}

.card.watcher .search-wrap .search.btn:focus {
  box-shadow: none;
}

.card.watcher .search-wrap .search.btn .btn-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card.watcher .tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.666rem;
  margin-top: 1.333rem;
}

.card.watcher .tag-list .tag {
  display: flex;
  align-items: center;
  gap: 0 0.55rem;
  height: 2.666rem;
  padding: 0.533rem 1rem 0.533rem 0.533rem;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  border-radius: 40px;
  background: #EDF1FF;
}

.card.watcher .tag-list .tag:hover {
  background-color: #DAE2FF;
}

.card.watcher .tag-list .tag .profile-img {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
}

.card.watcher .tag-list .tag button.tag-remove {
  width: 16px;
  height: 16px;
  margin-left: 0.25rem;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 12L4 4" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 4L4 12" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') center no-repeat;
  border: 0;
}

/* comment 영역 */
.card.comment .card-body {
  display: flex;
  align-items: stretch;
  padding: 0;
}

.card.comment .comment-title-area {
  display: flex;
  align-items: center;
  gap: 0 0.8rem;
}

.card.comment .comment-title-area h3 {
  margin-bottom: 0;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
}

.card.comment .comment-title-area .total-num {
  min-width: 36px;
  height: 25px;
  padding: 0 0.6rem;
  border-radius: 40px;
  color: #2E2E38;
  font-family: 'NanumGothic';
  font-size: 14px;
  font-weight: 700;
  line-height: 25px;
  text-align: center;
}

.card.comment .comment-area .comment-title-area .total-num {
  background-color: #FFE500;
}

.card.comment .highlight-area .comment-title-area .total-num {
  background-color: #2356F6;
  color: #FFF;
}

.card.comment .comment-area {
  display: flex;
  flex-direction: column;
  width: 65%;
  border-right: 1px solid #DDDDE3;
}

.card.comment .comment-list-wrap {
  flex-grow: 1;
  flex-shrink: 0;
}

.card.comment .comment-list {
  display: flex;
  flex-direction: column;
  gap: 2.666rem 0;
}

.card.comment .highlight-area {
  width: 35%;
  min-width: 26.133rem;
}

.card.comment .highlight-area .comment-list {
  height: calc(100% - 6.8rem);
  overflow-y: auto;
}


/* 하단 버튼 영역 */
.content-btn-wrap {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 1.333rem 3.125vw;
  gap: 0.666rem;
  align-items: center;
  z-index: 999;
  background-color: #f6f6fa;
  box-shadow: 0px -10px 30px 0px rgba(46, 46, 56, 0.14);
}

.content-btn-wrap.bottom {
  box-shadow: none;
}

.content-btn-wrap .btn-move-wrap {
  display: flex;
  gap: 0 0.8rem;
}

.content-btn-wrap .text-container .text {
  color: #747480;
  font-family: 'NanumGothic';
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
}

.content-btn-wrap .button-container {
  display: flex;
  gap: 0 10px;
  margin: 0;
  padding: 0;
}

/* ESG Data 영역 */
.esg-data-wrap .card .card-header {
  padding-bottom: 2rem;
}

.esg-data-wrap .card.fold .card-header {
  padding-bottom: 0;
  border-radius: 30px;
}

.esg-data-wrap .card.fold .card-body {
  height: 0;
  min-height: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  border-top: none !important;
}

.esg-data-wrap .card-title-area {
  display: flex;
  align-items: center;
  gap: 0 0.533rem;
}

.esg-data-wrap .card-title-area .icon {
  display: block;
  width: 24px;
  height: 24px;
}

.esg-data-wrap .card-title-area .title-text {
  font-family: 'NanumGothic';
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
}

.replies >>> .ql-container.ql-snow {
  border: none;
  border-radius: 0px 20px 20px 20px;
  background-color: #F6F6FA;
}

.comment-reply >>> .ql-editor {
  max-height: 16.133rem;
}
</style>